/**
 * Domain & Range generator.
 *
 * Given a relation as a set of ordered pairs (and, at higher difficulty, a
 * rendered plot of those points), identify the domain (set of x-values) or the
 * range (set of y-values). The answer is a SetAnswer built directly from the
 * pairs, so it is correct by construction; the student enters the values in
 * any order, comma- or "or"-separated.
 *
 * Families:
 *   D1: domain from ordered pairs      -> set
 *   D2: range from ordered pairs        -> set
 *   D3: domain/range from a plotted set  -> set (with graph)
 */

import { rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { makeSetAnswer } from "../engine/answer";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";
import type { CoordinateModel } from "../graph/plot-model";
import { getTopic } from "../curriculum/curriculum";

const TOPIC = "domain-range" as const;
const VERSION = "1.0.0";

type Family = "D1" | "D2" | "D3";

interface Cfg {
  readonly families: readonly Family[];
  readonly range: number;
  readonly count: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["D1", "D2"], range: 6, count: 3 },
  standard: { families: ["D1", "D2", "D3"], range: 8, count: 4 },
  hard: { families: ["D2", "D3"], range: 9, count: 5 },
  challenge: { families: ["D2", "D3"], range: 10, count: 5 },
};

export class DomainRangeGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = ["easy", "standard", "hard", "challenge"];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    const rng = new Rng(ctx.seed);
    const cfg = DIFF[ctx.difficulty];
    const family = rng.pick(cfg.families);
    // For D3 (from plot) or when asking domain, keep x-values distinct so the
    // relation is a clean function; that keeps the domain unambiguous.
    const askDomain = family === "D1" || (family === "D3" && rng.bool());

    const pairs: Array<{ x: number; y: number }> = [];
    const xsUsed = new Set<number>();
    while (pairs.length < cfg.count) {
      const x = rng.int(-cfg.range, cfg.range);
      if (xsUsed.has(x)) continue;
      xsUsed.add(x);
      pairs.push({ x, y: rng.int(-cfg.range, cfg.range) });
    }

    const values = askDomain ? pairs.map((p) => p.x) : pairs.map((p) => p.y);
    const answer = makeSetAnswer(values.map((v) => rat(v)));

    const pairsLatex = `\\{ ${pairs.map((p) => `(${p.x}, ${p.y})`).join(", ")} \\}`;
    const askWord = askDomain ? "domain" : "range";
    let promptText: string;
    let promptLatex: string;
    let graph: CoordinateModel | undefined;

    if (family === "D3") {
      promptText = `What is the ${askWord} of the plotted relation?`;
      promptLatex = `\\text{What is the ${askWord} of the plotted relation?}`;
      graph = {
        kind: "coordinate",
        xMin: -cfg.range - 1,
        xMax: cfg.range + 1,
        yMin: -cfg.range - 1,
        yMax: cfg.range + 1,
        points: pairs.map((p) => ({ x: rat(p.x), y: rat(p.y), closed: true })),
        segments: [],
      };
    } else {
      promptText = `What is the ${askWord} of ${pairsLatex}?`;
      promptLatex = `\\text{What is the ${askWord} of } ${pairsLatex}\\text{?}`;
    }

    // Sorted value list for the worked step.
    const sortedLatex =
      "\\{ " + answer.values.map((v) => v.toLatex()).join(", ") + " \\}";

    const q: Question = {
      id: `${TOPIC}-${ctx.seed}-${family}`,
      generator: TOPIC,
      generatorVersion: VERSION,
      seed: ctx.seed,
      unit: topic.unit,
      topic: TOPIC,
      family,
      difficulty: ctx.difficulty,
      prompt: { text: promptText, latex: promptLatex },
      answerType: "set",
      answer,
      steps: [
        {
          description: askDomain
            ? "The domain is the set of all x-values (first coordinates)."
            : "The range is the set of all y-values (second coordinates).",
          latex: sortedLatex,
        },
      ],
      hints: [
        { level: 1, text: askDomain ? "List the first number of each pair." : "List the second number of each pair." },
        { level: 2, text: "List each value once, in any order." },
      ],
      subskills: topic.subskills,
    };
    return graph ? { ...q, graph } : q;
  }

  signature(q: Question): QuestionSignature {
    const structure =
      q.answer.kind === "set" ? q.answer.values.map((v) => v.key()).join(",") : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const domainRange = new DomainRangeGenerator();
